<template>
  <div :class="$style.upload" gk-upload :disabled="disabled" @click="handleProxyClick">
    <input type="file" :class="$style.file" ref="file" :disabled="disabled" :multiple="multiple" :accept="accept" @change="previewFiles" />
  </div>
</template>

<script>
import VueTypes from 'vue-types'
import {ajax} from '../../utils/request'

/**
 * 上传组件
 * @module components/Upload
 * @author 徐晨
 * @see http://gitbook.geekbang.org/geek_ui/#/upload
 * @example
 * <gkUpload alias="fileAlias" :url="url" :postData="postData" :disabled="disabled" :validate="testType" :auto="autoUpload" @invalid="handleDemoUploadInvalid" @preview="handleDemoUploadPreview" @beforeUpload="handleDemoUploadReady" @uploadProgress="handleDemoUploadProgress" @uploadComplete="handleDemoUploadComplete" @uploadSuccess="handleDemoUploadSuccess" @uploadError="handleDemoUploadError" />
 */
export default {
  name: 'gkui-upload',
  /**
   * 传入数据
   * @prop {String}    alias            上传数据的 key 值
   * @prop {Object}    validate         上传数据的验证方法
   * @prop {Boolean}  [auto=true]       选择文件后是否自动上传
   * @prop {Function} [request]         上传数据的方法，默认为 utils/request
   * @prop {String}    url              上传的地址 url
   * @prop {Object}   [postData]        上传附加数据
   * @prop {Boolean}  [disabled=false]  是否禁用
   * @prop {Boolean}  [multiple=false]  是否支持多文件上传
   * @prop {String}    accept           接受的文件类型
   */
  props: {
    alias: VueTypes.string.isRequired,
    validate: VueTypes.object.def({}),
    auto: VueTypes.bool.def(true),
    request: Function,
    url: VueTypes.string.isRequired,
    postData: Object,
    disabled: VueTypes.bool.def(false),
    multiple: VueTypes.bool.def(false),
    accept: VueTypes.string.def('')
  },
  data () {
    return {
      file: null,
      formData: null
    }
  },
  /**
   * 计算属性
   */
  computed: {
    xhr () {
      return this.request || ajax
    }
  },
  methods: {
    /**
     * 点击触发上传事件
     */
    handleProxyClick () {
      if (this.disabled) {
        return
      }

      this.$refs.file.click()
    },
    /**
     * 清空 input，防止上传同样文件不能触发 change 事件
     */
    clear () {
      this.$refs.file.value = ''
    },
    /**
     * @method previewFiles
     * @desc 选择文件事件
     */
    previewFiles (event) {
      /**
       * 如果开启多文件，将文件抛出。剩下的逻辑在业务方处理
       */
      if (this.multiple) {
        this.$emit('change', this.$refs.file.files)
        return
      }

      const file = this.$refs.file.files[0]

      if (!file) {
        return
      }

      let valid = true

      this.formData = null

      this.file = file

      Object.keys(this.validate).forEach(key => {
        if (valid) {
          const result = this.validate[key](this.file)

          if (!result) {
            valid = false
            this.$emit('invalid', key, this.file)
          }
        }
      })

      if (!valid) {
        return
      }

      let formData = this.formatData(this.file)

      this.formData = formData

      const reader = new FileReader()

      reader.addEventListener('load', () => {
        this.$emit('preview', file.type, reader.result, file)
      }, false)

      file && reader.readAsDataURL(file)

      this.$emit('beforeUpload', formData, () => {
        this.uploadFile()
      })

      this.auto && this.uploadFile()
    },
    /**
     * @func uploadFile
     * @desc 上传文件
     */
    uploadFile () {
      const option = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        url: this.url,
        type: 'post',
        data: this.formData,
        /**
         * @param {Number} per            已上传百分比
         * @param {Number} loaded         已上传字节
         * @param {Number} total          总共需要上传字节
         * @param {Event}  progressEvent  event
         */
        progress: (progressEvent) => {
          const {loaded, total} = progressEvent
          const per = Math.floor(loaded / total) * 100

          this.$emit('progress', per, loaded, total, progressEvent)
        }
      }

      this.xhr(option).then(res => {
        this.$emit('complete', true, res)
        this.$emit('success', res)

        this.$refs.file.value = ''
      }).catch(err => {
        this.$emit('complete', false, err)
        this.$emit('error', err)

        this.$refs.file.value = ''
      })
    },
    /**
     * @func formatData
     * @desc 整理上传的数据
     * @param  {Object} file       上传的文件数据
     * @param  {String} file.name  上传的文件名称
     * @param  {String} file.type  上传的文件类型
     * @param  {Number} file.size  上传的文件大小
     */
    formatData (file) {
      let form = new FormData()

      form.append(this.alias, file, encodeURIComponent(file.name))

      this.postData && Object.keys(this.postData).forEach(key => {
        form.append(key, this.postData[key])
      })

      return form
    }
  }
}
</script>

<style lang="scss" module>
.upload {
  cursor: pointer;

  &[disabled] {
    cursor: not-allowed;
  }
}
.file {
  visibility: hidden;
  width: 100%; height: 100%;
  opacity: 0;
}
</style>

